// Die Zeit eines Ereignisses in Worten — Liste, Detail und Vorlage sprechen
// dieselben. Ein Termin ist ein TAG, keine Uhrzeit, und er hat eine
// Genauigkeit: „Oktober 2026" ist eine andere Aussage als „1. Oktober 2026".

import type { MarketEvent } from '../../api/client'

const WOCHENTAGE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag',
                    'Freitag', 'Samstag']
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli',
                'August', 'September', 'Oktober', 'November', 'Dezember']

/** JJJJ-MM-TT → Ortszeit-Datum. Nicht `new Date(iso)`: Das wäre UTC. */
export function alsTag(iso: string): Date {
  const [j, m, t] = iso.split('-').map(Number)
  return new Date(j, m - 1, t)
}

/**
 * Ein Tag relativ zu heute, als JJJJ-MM-TT.
 *
 * IN ORTSZEIT gerechnet und nicht über `toISOString`: Das liefert UTC, und
 * wer um halb eins nachts in Deutschland auf die Seite geht, bekäme den
 * Vortag als „heute".
 */
export function tagVersetzt(tage: number): string {
  const tag = new Date()
  tag.setDate(tag.getDate() + tage)
  const zwei = (n: number) => String(n).padStart(2, '0')
  return `${tag.getFullYear()}-${zwei(tag.getMonth() + 1)}-${zwei(tag.getDate())}`
}

/** 06.09.2026 */
export function tagKurz(iso: string): string {
  const [j, m, t] = iso.split('-')
  return `${t}.${m}.${j}`
}

/** 06.09. — für „bis" in der Zeile, wo das Jahr schon feststeht. */
export function tagKuerzer(iso: string): string {
  const [, m, t] = iso.split('-')
  return `${t}.${m}.`
}

/** Samstag */
export function wochentag(iso: string): string {
  return WOCHENTAGE[alsTag(iso).getDay()]
}

/** Samstag, 6. September 2026 */
export function tagVoll(iso: string): string {
  const d = alsTag(iso)
  return `${WOCHENTAGE[d.getDay()]}, ${d.getDate()}. ${MONATE[d.getMonth()]} ${d.getFullYear()}`
}

/** September 2026 */
export function monat(iso: string): string {
  const d = alsTag(iso)
  return `${MONATE[d.getMonth()]} ${d.getFullYear()}`
}

/** Q4 2026 */
export function quartal(iso: string): string {
  const d = alsTag(iso)
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`
}

/** Ob der Termin auf einen Tag genau steht. */
export function ungenau(e: MarketEvent): boolean {
  return !e.date_from || e.date_precision !== 'EXACT'
}

/**
 * Der Termin, mit seiner Genauigkeit gezeichnet.
 *
 * „≈" markiert Schätzungen; ein Monat oder Quartal steht als solches da, weil
 * ein erfundener Tag eine Aussage behauptete, die es nicht gibt.
 */
export function zeitraum(e: MarketEvent): string {
  if (!e.date_from) return 'Zeitpunkt offen'
  switch (e.date_precision) {
    case 'DATE_RANGE':
      return e.date_to
        ? `${tagKuerzer(e.date_from)} – ${tagKurz(e.date_to)}`
        : tagKurz(e.date_from)
    case 'MONTH': return monat(e.date_from)
    case 'QUARTER': return quartal(e.date_from)
    case 'ESTIMATED': return `≈ ${tagKurz(e.date_from)}`
    case 'UNKNOWN': return 'Zeitpunkt offen'
    default: return tagKurz(e.date_from)
  }
}

/** Was hinter dem Termin steht — bei einem festen Tag nichts. */
export function zeitHinweis(e: MarketEvent): string {
  if (!e.date_from) return 'Zeitpunkt offen'
  switch (e.date_precision) {
    case 'DATE_RANGE': return 'mehrtägig'
    case 'MONTH': return 'Tag offen'
    case 'QUARTER': return 'Quartal'
    case 'ESTIMATED': return 'geschätzt'
    case 'UNKNOWN': return 'Zeitpunkt offen'
    default: return ''
  }
}

/** Die lange Form für den Kopf des Details. */
export function datumVoll(e: MarketEvent): string {
  return e.date_from && e.date_precision === 'EXACT'
    ? tagVoll(e.date_from) : zeitraum(e)
}
